import {MarginOutlined} from '@mui/icons-material';
import { AUDIENCE_EXPORT_TYPES, ENUM_REGEX } from '../../../constants/index';

import {
  LIVERAMP_SEGMENT_FIELD_NAME,
  META_MODEL_CUT_SIZE,
  META_SEGMENT_FIELD_NAME,
  TRADE_DESK_SEGMENT_FIELD_NAME,
  TIKTOK_SEGMENT_FIELD_NAME,
  TIKTOK_MOBILE_OS_ALL,
  TIKTOK_MOBILE_OS_ANDROID,
  TIKTOK_MOBILE_OS_IOS,
  TIKTOK_AUDIENCE_SIZE_NARROW,
  TIKTOK_AUDIENCE_SIZE_BALANCED,
  TIKTOK_AUDIENCE_SIZE_BROAD,
  TIKTOK_PLACEMENT_TIKTOK,
  TIKTOK_PLACEMENT_PANGLE,
  TIKTOK_PLACEMENT_GLOBAL_APP_BUNDLE,
  TIKTOK_PLACEMENTS_TOOLTIP,
  YAHOO_DSP_SEGMENT_FIELD_NAME,
  REDDIT_SEGMENT_FIELD_NAME,
} from '../../../content';
import { nanoid } from 'nanoid';

/**
 * Configuration for Trade Desk Custom Segment fields.
 */
const tradeDeskCustomSegmentConfig = [
  {
    type: 'textbox',
    id: 'partnerID',
    column: 'partnerID',
    label: TRADE_DESK_SEGMENT_FIELD_NAME.PARTNER_ID,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 5 },
    value: '',
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
  },
  {
    type: 'label',
    label: 'Or',
    gridProps: { xs: 2 },
    isVisible: true,
  },
  {
    type: 'textbox',
    id: 'advertiserID',
    column: 'advertiserID',
    label: TRADE_DESK_SEGMENT_FIELD_NAME.ADVERTISER_ID,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 5 },
    value: '',
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
  },
];

/**
 * Configuration for LiveRamp Custom Segment fields.
 */
const liveRampCustomSegmentConfig = [
  {
    type: 'textbox',
    id: 'tvTargeting',
    column: 'tvTargeting',
    label: LIVERAMP_SEGMENT_FIELD_NAME.TV_TARGETING,
    regex: ENUM_REGEX.DECIMAL, // Only decimal values are allowed.
    variant: 'outlined',
    required: true,
    gridProps: { xs: 5 },
    value: '',
    isVisible: true,
    InputLabelProps: { shrink: true },
  },
  {
    id: 'space', // Placeholder for spacing between fields.
    gridProps: { xs: 2 },
    isVisible: true,
  },
  {
    type: 'textbox',
    id: 'advertiserDirect',
    column: 'advertiserDirect',
    required: false,
    label: LIVERAMP_SEGMENT_FIELD_NAME.ADVERTISER_DIRECT,
    regex: ENUM_REGEX.WHOLE_NUMBER,
    variant: 'outlined',
    gridProps: { xs: 5 },
    value: '',
    isVisible: true,
    InputLabelProps: { shrink: true },
  },
  {
    type: 'autocomplete',
    id: 'platformDestination',
    column: 'platformDestination',
    placeholder: LIVERAMP_SEGMENT_FIELD_NAME.CHOOSE_INTEGRATION,
    variant: 'outlined',
    options: [],
    required: false,
    gridProps: { xs: 12 },
    value: '',
    isVisible: true,
  },
  {
    type: 'autocomplete',
    id: 'advertiserDestination',
    column: 'advertiserDestination',
    placeholder: LIVERAMP_SEGMENT_FIELD_NAME.CHOOSE_ADVERTISER,
    variant: 'outlined',
    options: [],
    required: true,
    gridProps: { xs: 12 },
    value: '',
    disabled: true,
    isVisible: true,
  },
];

/**
 * Configuration for LiveRamp OTS Segment fields.
 */
const liveRampOtsSegmentConfig = [
  {
    type: 'textbox',
    id: 'tvTargeting',
    column: 'tvTargeting',
    label: LIVERAMP_SEGMENT_FIELD_NAME.TV_TARGETING,
    regex: ENUM_REGEX.DECIMAL, // Only decimal values are allowed.
    variant: 'outlined',
    required: true,
    gridProps: { xs: 5 },
    value: '',
    isVisible: true,
    InputLabelProps: { shrink: true },
  },
  {
    id: 'space', // Placeholder for spacing between fields.
    gridProps: { xs: 2 },
    isVisible: true,
  },
  {
    type: 'textbox',
    id: 'advertiserDirect',
    column: 'advertiserDirect',
    label: LIVERAMP_SEGMENT_FIELD_NAME.ADVERTISER_DIRECT,
    regex: ENUM_REGEX.WHOLE_NUMBER,
    variant: 'outlined',
    gridProps: { xs: 5 },
    value: '',
    isVisible: true,
    InputLabelProps: { shrink: true },
  },
];

/**
 * Configuration for Trade Desk OTS Segment fields.
 */
const tradeDeskOTSSegmentConfig: any[] = [];

/**
 * Configuration for Meta Custom Segment fields.
 */
const metaCustomSegmentConfig = [
  {
    type: 'textbox',
    id: 'metaAdAccountId',
    column: 'metaAdAccountId',
    label: META_SEGMENT_FIELD_NAME.META_AD_ACCOUNT_ID,
    regex: ENUM_REGEX.WHOLE_NUMBER, // Only whole numbers are allowed for Meta Ad Account ID
    helperText: 'Digits only, no "act_"',
    variant: 'outlined',
    required: true,
    gridProps: { xs: 12 },
    value: '',
    disabled: false,
    isVisible: true,
    isAllowPaste: true,
    InputLabelProps: { shrink: true },
  },
  {
    type: 'dropdown',
    id: 'metaLalRatio',
    column: 'metaLalRatio',
    label: META_SEGMENT_FIELD_NAME.MODEL_CUT_SIZE,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 3 },
    value: '',
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
    style: { marginTop: 5 },
    options: [
      { value: 1, label: '1' },
      { value: 2, label: '2' },
      { value: 3, label: '3' },
      { value: 4, label: '4' },
      { value: 5, label: '5' },
      { value: 6, label: '6' },
      { value: 7, label: '7' },
      { value: 8, label: '8' },
      { value: 9, label: '9' },
      { value: 10, label: '10' },
    ],
  },
  {
    type: 'label',
    id: 'metaLalRatioLabel',
    label: '',
    isVisible: true,
    gridProps: { xs: 6 },
    style: {
      marginTop: 22,
      marginLeft: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'start',
    },
  },
];

/**
 * Configuration for Yahoo DSP OTS Segment fields.
 */
const yahooDspOTSSegmentConfig = [
  {
    type: 'textbox',
    id: 'tvCpm',
    column: 'tvCpm',
    label: YAHOO_DSP_SEGMENT_FIELD_NAME.TV_CPM,
    regex: ENUM_REGEX.DECIMAL, // Only decimal values are allowed.
    variant: 'outlined',
    required: true,
    gridProps: { xs: 5 },
    value: '',
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
  },
];

/**
 * Configuration for Yahoo DSP Custom Segment fields.
 */
const yahooDspCustomSegmentConfig = [
  {
    type: 'textbox',
    id: 'partnerID',
    column: 'partnerID',
    label: YAHOO_DSP_SEGMENT_FIELD_NAME.PARTNER_ID,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 5 },
    value: '',
    isVisible: true,
    InputLabelProps: { shrink: true },
  },
  {
    type: 'label',
    label: '',
    gridProps: { xs: 2 },
    isVisible: true,
  },
  {
    type: 'textbox',
    id: 'tvCpm',
    column: 'tvCpm',
    label: YAHOO_DSP_SEGMENT_FIELD_NAME.TV_CPM,
    regex: ENUM_REGEX.DECIMAL,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 5 },
    value: '',
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
  },
];

/**
 * Configuration for TikTok Custom Segment fields.
 */
const tiktokCustomSegmentConfig = [
  {
    type: 'textbox',
    id: 'advertiserID',
    column: 'advertiserID',
    label: TIKTOK_SEGMENT_FIELD_NAME.ADVERTISER_ID,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 12 },
    value: '',
    disabled: false,
    isVisible: true,
    isAllowPaste: true,
    InputLabelProps: { shrink: true },
  },
  {
    type: 'dropdown',
    id: 'mobileOs',
    column: 'mobileOs',
    label: TIKTOK_SEGMENT_FIELD_NAME.MOBILE_OS,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 6 },
    style: { marginTop: 5, marginRight: '8px' },
    value: '',
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
    options: [
      { value: '0', label: TIKTOK_MOBILE_OS_ALL },
      { value: '1', label: TIKTOK_MOBILE_OS_ANDROID },
      { value: '2', label: TIKTOK_MOBILE_OS_IOS },
    ],
  },
  {
    type: 'dropdown',
    id: 'audienceModelingBreadth',
    column: 'audienceModelingBreadth',
    label: TIKTOK_SEGMENT_FIELD_NAME.AUDIENCE_SIZE,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 6 },
    style: { marginTop: 5, marginLeft: '8px', marginRight: '8px' },
    value: '',
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
    options: [
      { value: 'modeling_breadth_narrow', label: TIKTOK_AUDIENCE_SIZE_NARROW },
      { value: 'modeling_breadth_balanced', label: TIKTOK_AUDIENCE_SIZE_BALANCED },
      { value: 'modeling_breadth_broad', label: TIKTOK_AUDIENCE_SIZE_BROAD },
    ],
  },
  {
    type: 'multiselect',
    id: 'placements',
    column: 'placements',
    label: TIKTOK_SEGMENT_FIELD_NAME.PLACEMENTS,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 6 },
    value: [],
    disabled: false,
    isVisible: true,
    InputLabelProps: { shrink: true },
    options: [
      { value: 'tiktok_placement_tiktok', label: TIKTOK_PLACEMENT_TIKTOK },
      { value: 'tiktok_placement_pangle', label: TIKTOK_PLACEMENT_PANGLE },
      { value: 'tiktok_placement_global_app_bundle', label: TIKTOK_PLACEMENT_GLOBAL_APP_BUNDLE },
    ],
    tooltip: TIKTOK_PLACEMENTS_TOOLTIP,
  },
];

/**
 * Configuration for Reddit Custom Segment fields.
 */
const redditCustomSegmentConfig = [
  {
    type: 'textbox',
    id: 'platformDestination',
    column: 'platformDestination',
    label: REDDIT_SEGMENT_FIELD_NAME.REDDIT_AD_ACCOUNT_ID,
    variant: 'outlined',
    required: true,
    gridProps: { xs: 12 },
    value: '',
    disabled: false,
    isVisible: true,
    isAllowPaste: true,
    InputLabelProps: { shrink: true },
  },
];

/**
 * Fetches the form fields for a specific audience export type.
 *
 * @param exportType - The type of audience export (e.g., CUSTOM_SEGMENT, LIVERAMP_CUSTOM_SEGMENT).
 * @returns The array of form fields that are visible for the given export type.
 */
const getAudienceExportField = (exportType: string) => {
  switch (exportType) {
    case AUDIENCE_EXPORT_TYPES.OTS_SEGMENT:
    case AUDIENCE_EXPORT_TYPES.TTD_OTS_SEGMENT_SEED_AUDIENCE:
      return tradeDeskOTSSegmentConfig.filter((field) => field.isVisible);

    case AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT:
    case AUDIENCE_EXPORT_TYPES.TTD_CUSTOM_SEGMENT_SEED_AUDIENCE:
      return tradeDeskCustomSegmentConfig.filter((field) => field.isVisible);

    case AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT:
      return liveRampCustomSegmentConfig.filter((field) => field.isVisible);

    case AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT:
    case AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE:
      return liveRampOtsSegmentConfig.filter((field) => field.isVisible);

    case AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT:
    case AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE:
      return liveRampCustomSegmentConfig.filter((field) => field.isVisible);

    case AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT_SEED_AUDIENCE:
      return metaCustomSegmentConfig.filter((field) => field.isVisible);

    case AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT_SEED_AUDIENCE:
      return tiktokCustomSegmentConfig.filter((field) => field.isVisible);

    case AUDIENCE_EXPORT_TYPES.YAHOO_DSP_OTS_SEGMENT:
      return yahooDspOTSSegmentConfig.filter((field) => field.isVisible);

    case AUDIENCE_EXPORT_TYPES.YAHOO_DSP_CUSTOM_SEGMENT:
      return yahooDspCustomSegmentConfig.filter((field) => field.isVisible);

    case AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT:
      return tiktokCustomSegmentConfig.filter((field) => field.isVisible);

    case AUDIENCE_EXPORT_TYPES.REDDIT_CUSTOM_SEGMENT:
      return redditCustomSegmentConfig.filter((field) => field.isVisible);

    default:
      return [];
  }
};

/**
 * Generates the audience export fields based on the provided export types.
 *
 * @param exportTypes - An array of export types to generate fields for.
 * @returns An array of objects containing the export type and its corresponding form fields.
 */
export const getAudienceExportFields = (exportTypes: string[] = []) => {
  const fields = [];

  for (const exportType of exportTypes) {
    fields.push({
      id: nanoid(), // Unique identifier for each field.
      exportType,
      formJSON: getAudienceExportField(exportType),
    });
  }

  return fields;
};
